import {logger} from '../../../logger';
import {observer} from 'mobx-react';
import React, {useCallback} from 'react';
import {runInAction} from 'mobx';
import s from '../../s';
import {add_to_cart} from '../../api';
import ATCButtonTemplate from './atc-button-template';
import {getGradYearAtcError} from '../grad-year/grad-year-util';
import {imageUploadStore} from '../image-upload/image-upload.store';
import {isRequiredUploadBlockingAtc} from './atc-required-upload';
import {isCouncilChosen} from '../lino/lino-store';
import {getRequiredUVLineError} from '../uv-print/uv-template';
import {instPlateStore} from '../engraved-plates/inst-plate/inst-plate.store';

type Props = {
    label?: string;
    buyNow?: boolean;
    additionalClass?: string;
    /**
     * The product page redesign's own check, first after the busy check: false stops here (an open size question). The control
     * passes none.
     */
    beforeAdd?: () => boolean;
};

const ATCButton: React.FC<Props> = observer(({buyNow, additionalClass, label, beforeAdd}) => {
    const atc = useCallback(
        async (buyNowParam = false) => {
            // Shared flight already owns busy; avoid stacking UI-only prechecks while in flight.
            if (s.ui.atc_busy) {
                return;
            }

            if (beforeAdd && !beforeAdd()) {
                return;
            }

            const gradYearError = getGradYearAtcError(s.ui.show_grad_year, s.ui.grad_year_input || '', s.ps.grad_year || '', s.prod.YearOfGraduation_Required);
            if (gradYearError) {
                runInAction(() => {
                    s.ps.atc_error = gradYearError;
                });
                return;
            }

            // A required UV template (the INFORMS awards): every editable line must be filled.
            const uvLineError = getRequiredUVLineError(s.prod.uv_options, s.prod.uv_lines, s.ps.is_uv_print);
            if (uvLineError) {
                runInAction(() => {
                    s.ps.atc_error = uvLineError;
                });
                return;
            }

            if (s.prod.IsCAF && !(s.ps.caf_input_sizes || s.ps.caf_valid_sizes)) {
                runInAction(() => {
                    s.ui.show_caf_doc_sizes = true;
                });
                return;
            }

            if (isRequiredUploadBlockingAtc(s.prod.allowUpload2, s.ui.image_uploaded, imageUploadStore.hasRetryableLocalUpload)) {
                runInAction(() => {
                    s.ui.image_uploaded_msg = `Please upload a ${s.prod.DocType} image before adding to cart`;
                });
                return;
            }

            if (s.si.StoreID === 205164 && ((s.prod.linos && s.prod.linos.length && (!s.ps.lino_text || !isCouncilChosen(s.prod.linos, s.ps.lino_id))) || (s.prod.ProductID === 88937 && (!s.ps.custom_text_1 || s.ps.custom_text_1.length < 5)))) {
                runInAction(() => {
                    s.ps.atc_error = `A selection for ${s.si.ProductLinoSelectionText || 'Embossed Text'} is required.`;
                });
                return;
            }

            // Personalized plate (INST-PLATES D6), before the template-line check of add_to_cart: an
            // incomplete plate (no template, a blank or invalid line, the certification unticked)
            // opens "Personalization Required"; a complete one opens the proof, whose "Confirm & Add
            // to Cart" runs add_to_cart; a confirmed one goes straight to the cart.
            if (s.isInstPlate && instPlateStore.beforeAddToCart(buyNowParam) !== 'add') {
                return;
            }

            try {
                await add_to_cart(buyNowParam);
            } catch (err) {
                logger.error(err);
            }
        },
        [beforeAdd],
    );

    return (
        <ATCButtonTemplate
            label={label}
            additionalClass={additionalClass}
            clicked={() => atc(buyNow)}
        />
    );
});

export default ATCButton;
